import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import useApi from "../../../../../shared/hooks/useApi";
import type { CaregiverId, OfficeId, PlanOfCareItemId } from "../../../../../shared/schema/schema";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";

/**
 * One selectable duty: an office's plan-of-care item. A caregiver associated
 * with several offices sees each office's catalogue, prefixed by office name so
 * two offices' "Bathing" are told apart.
 */
export type ValidatedDutyOption = {
  value: PlanOfCareItemId;
  label: string;
  officeName: string;
  section: string;
};

/**
 * Which plan-of-care items a caregiver is validated to perform in this agency.
 *
 * Read and replaced as a whole set through the caregiver validations endpoint.
 * Consulted by the visit duty resolver only for duties a plan marks as restricted
 * to validated caregivers, so an empty list changes nothing for unrestricted
 * duties.
 */
export function useCaregiverProfileValidatedDutiesController(
  caregiverId: CaregiverId,
  /** The offices the caregiver works for, whose catalogues are offered. */
  caregiverOfficeIds: OfficeId[] | undefined
) {
  const { api, queries } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const [draft, setDraft] = React.useState<PlanOfCareItemId[] | null>(null);
  const queryKey = ["caregiverProfileModal", caregiverId, "planOfCareItemValidations"];

  const validationsQuery = useQuery({
    queryKey,
    queryFn: () =>
      api.get("./caregivers/:caregiverId/plan_of_care_item_validations", {
        path: { caregiverId },
      }),
  });

  // The catalogue of every office the caregiver works for. `settings` lists
  // each office's plan of care type with its active items.
  const settingsQuery = useQuery(queries.planOfCareType.settings());
  const officeIds = React.useMemo(() => new Set(caregiverOfficeIds ?? []), [caregiverOfficeIds]);
  const officePlans = React.useMemo(
    () =>
      (settingsQuery.data?.planOfCares ?? []).filter(
        (plan) => plan.planOfCareTypeId !== null && officeIds.has(plan.officeId)
      ),
    [settingsQuery.data, officeIds]
  );

  // The full item list per type, inactive ones included, so a validation on a
  // since-deactivated item still displays instead of silently disappearing.
  const itemsQueries = useQueries({
    queries: officePlans.map((plan) => queries.planOfCareType.items(plan.planOfCareTypeId!)),
  });

  const options = React.useMemo<ValidatedDutyOption[]>(() => {
    const multipleOffices = officePlans.length > 1;
    return officePlans.flatMap((plan, index) =>
      (itemsQueries[index]?.data?.items ?? [])
        .filter((item) => item.type === "Task")
        .map((item) => ({
          value: item.id,
          label: `${multipleOffices ? `${plan.officeName} · ` : ""}${item.label} (${item.code})${
            item.isActive ? "" : " (inactive)"
          }`,
          officeName: plan.officeName,
          section: item.section,
        }))
    );
  }, [officePlans, itemsQueries]);

  const updateMutation = useMutation({
    mutationFn: (planOfCareItemIds: PlanOfCareItemId[]) =>
      api.put("./caregivers/:caregiverId/plan_of_care_item_validations", {
        path: { caregiverId },
        body: { planOfCareItemIds },
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(queryKey, response);
      setDraft(null);
      toast({ description: "Validated duties updated successfully.", status: "success" });
    },
    onError: () => {
      toast({ description: "Could not update validated duties.", status: "error" });
    },
  });

  const validatedItemIds = validationsQuery.data?.planOfCareItemIds;

  const toggleEditMode = () => {
    setDraft((currentDraft) => {
      if (currentDraft !== null) {
        return null;
      }
      return validatedItemIds === undefined ? currentDraft : [...validatedItemIds];
    });
  };

  return {
    draft,
    isEditing: draft !== null,
    isLoadingOptions: settingsQuery.isLoading || itemsQueries.some((query) => query.isLoading),
    isUpdating: updateMutation.isPending,
    options,
    refetch: validationsQuery.refetch,
    save: (planOfCareItemIds: PlanOfCareItemId[]) => updateMutation.mutate(planOfCareItemIds),
    setDraft,
    validatedItemIds,
    validationsQuery,
    toggleEditMode,
  };
}
